/**
 * Place online order: build full bookRequest payload and normalize response.
 * See online_orders_feature.md and online_orders_implementation_gaps.md.
 */
import { ApiClient } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';

export interface PlaceOrderItem {
  id: number;
  quantity: number;
  price?: number;
}

export interface PlaceOrderAddress {
  id?: number;
  area_id?: number;
  name?: string;
  phone?: string;
  mail?: string;
  email?: string;
  address?: string;
  full_address?: string;
  governorate_id?: number;
  address_name?: string;
  block?: string;
  street?: string;
  house_name?: string;
  avenue?: string;
  floor?: string;
  flat?: string;
  landmark?: string;
  zip_code?: string;
  notes?: string;
  driver_note?: string;
  PACI_number?: string;
  latitude?: number;
  longitude?: number;
  [key: string]: unknown;
}

export interface PlaceOrderParams {
  customer_id: number;
  items: PlaceOrderItem[];
  address: PlaceOrderAddress;
  subtotal: number;
  delivery_cost: number;
  discount?: number;
  coupon_code?: string;
  payment_gateway?: string;
  payment_type?: number;
  redeem_from_loyalty?: number;
  device_id?: string;
  /** Customer email — sent as both `mail` and `email` (API validates `email`). */
  mail?: string;
  email?: string;
}

export type PlaceOrderResult = {
  chargeUrl: string;
  orderId: string | number | null;
  /** True when amount is zero / already captured — no WebView expected. */
  paymentSettled: boolean;
  cost: number;
};

function resolveOrderEmail(
  param_mail: string | undefined,
  param_email: string | undefined,
  address: PlaceOrderAddress,
): string {
  const candidates = [
    param_email,
    param_mail,
    address.mail,
    typeof address.email === 'string' ? address.email : undefined,
  ];
  for (const value of candidates) {
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

function buildAddressDataJson(addr: PlaceOrderAddress): string {
  const obj = {
    governorate_id: addr.governorate_id ?? 0,
    address_name: addr.address_name ?? addr.name ?? '',
    block: addr.block ?? '',
    street: addr.street ?? '',
    house_name: addr.house_name ?? '',
    avenue: addr.avenue ?? '',
    floor: addr.floor ?? '',
    flat: addr.flat ?? '',
    landmark: addr.landmark ?? '',
    zip_code: addr.zip_code ?? '',
    notes: addr.notes ?? '',
    driver_note: addr.driver_note ?? '',
    PACI_number: addr.PACI_number ?? '',
    latitude: addr.latitude ?? 0,
    longitude: addr.longitude ?? 0,
  };
  return JSON.stringify(obj);
}

function as_record(value: unknown): Record<string, unknown> | null {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function pick_url(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return '';
}

/** Accept common payment-link field names across gateways / response shapes. */
export function extractOrderChargeUrl(response: unknown): string {
  const root = as_record(response);
  const data = as_record(root?.data);
  const order = as_record(data?.order) ?? as_record(root?.order) ?? data;
  const payment = as_record(order?.payment) ?? as_record(data?.payment) ?? as_record(root?.payment);
  const transaction = as_record(order?.transaction) ?? as_record(data?.transaction);

  return pick_url(
    order?.charge_url,
    order?.payment_url,
    order?.redirect_url,
    order?.url,
    data?.charge_url,
    data?.payment_url,
    data?.redirect_url,
    data?.url,
    root?.charge_url,
    root?.payment_url,
    root?.redirect_url,
    payment?.charge_url,
    payment?.payment_url,
    payment?.url,
    transaction?.charge_url,
    transaction?.payment_url,
    transaction?.url,
  );
}

export function extractOrderId(response: unknown): string | number | null {
  const root = as_record(response);
  const data = as_record(root?.data);
  const order = as_record(data?.order) ?? as_record(root?.order);
  const raw =
    order?.id ??
    data?.order_id ??
    data?.id ??
    root?.order_id ??
    root?.id ??
    null;
  if (raw == null || raw === '') {
    return null;
  }
  return raw as string | number;
}

function is_order_payment_settled(response: unknown, cost: number): boolean {
  if (extractOrderChargeUrl(response)) {
    return false;
  }
  if (cost <= 0) {
    return true;
  }
  const root = as_record(response);
  const data = as_record(root?.data);
  const order = as_record(data?.order) ?? as_record(root?.order) ?? data;
  if (!order) {
    return false;
  }
  const payment_status = String(order.payment_status ?? order.status ?? '')
    .trim()
    .toLowerCase();
  return ['captured', 'paid', 'success', 'completed', 'free'].includes(payment_status);
}

/**
 * Build full bookRequest and POST order. Returns charge_url and order id.
 */
export async function placeOrder(params: PlaceOrderParams): Promise<PlaceOrderResult> {
  const {
    customer_id,
    items,
    address,
    subtotal,
    delivery_cost,
    discount = 0,
    coupon_code = '',
    payment_gateway = 'knet',
    payment_type = 1,
    redeem_from_loyalty = 0,
    device_id = '0',
    mail: param_mail,
    email: param_email,
  } = params;

  const resolved_email = resolveOrderEmail(param_mail, param_email, address);

  const total_before_redeem = subtotal + delivery_cost - discount;
  const cost = Math.max(0, total_before_redeem - redeem_from_loyalty);
  const cost_rounded = Math.round(cost * 1000) / 1000;

  const menu_items = JSON.stringify(
    items.map(i => ({ menu_item_id: i.id, qty: i.quantity || 1 })),
  );

  const address_data = buildAddressDataJson(address);

  const body = {
    address_id: address.id ?? 0,
    address_data,
    device_id,
    customer_id,
    phone: address.phone ?? '',
    name: address.name ?? '',
    mail: resolved_email,
    email: resolved_email,
    area_id: address.area_id ?? 0,
    cost: cost_rounded,
    menu_items,
    payment_gateway,
    payment_type,
    redeem_from_loyalty,
    coupon_code: coupon_code || '',
  };

  const response: unknown = await ApiClient.post(endpoints.order, body);
  const root = as_record(response);

  if (root?.success === false) {
    throw new Error(String(root?.message || 'Order failed'));
  }

  const chargeUrl = extractOrderChargeUrl(response);
  const orderId = extractOrderId(response);
  const paymentSettled = is_order_payment_settled(response, cost_rounded);

  return {
    chargeUrl,
    orderId,
    paymentSettled,
    cost: cost_rounded,
  };
}
